import { LearningService } from '@educator/libs/educatorApi'
import type {
  GetLiveLessonData,
  GetSubjectDetailData,
  LiveLessonQuiz,
  LiveLessonResponse,
  LiveLessonResults,
  SaveLiveLessonQuizAnswerData,
  StartLiveLessonQuizAttemptData,
  SubjectDetailResponse,
  SubmitLiveLessonQuizData,
  UpdateVideoProgressData,
  VideoProgress,
} from '@mc/educator-api'
import { getApiErrorStatus } from '../../utils/apiErrors'

export const liveLessonApi = {
  getLiveLesson: (data: GetLiveLessonData): Promise<LiveLessonResponse> =>
    LearningService.getLiveLesson(data),

  startLiveLessonQuizAttempt: (
    data: StartLiveLessonQuizAttemptData,
  ): Promise<LiveLessonQuiz> =>
    LearningService.startLiveLessonQuizAttempt(data),

  saveLiveLessonQuizAnswer: (
    data: SaveLiveLessonQuizAnswerData,
  ): Promise<void> => LearningService.saveLiveLessonQuizAnswer(data),

  submitLiveLessonQuiz: (
    data: SubmitLiveLessonQuizData,
  ): Promise<LiveLessonResults> => LearningService.submitLiveLessonQuiz(data),

  updateVideoProgress: (
    data: UpdateVideoProgressData,
  ): Promise<VideoProgress> => LearningService.updateVideoProgress(data),

  getSubjectDetail: (
    data: GetSubjectDetailData,
  ): Promise<SubjectDetailResponse> => LearningService.getSubjectDetail(data),
}

const transientOnly = (failureCount: number, error: unknown): boolean => {
  const status = getApiErrorStatus(error)
  if (status !== undefined && status < 500) return false
  return failureCount < 3
}

// Query retry for the live-lesson reads: every 4xx is definitive, so only 5xx
// and network errors are worth repeating.
export const retryLiveLessonTransientOnly = transientOnly

// Mutation retry. React Query gives mutations ZERO retries by default and that
// default is deliberate — repeating a write can apply it twice. Opt in ONLY
// where the contract guarantees a replay is a no-op: PUT/PATCH/DELETE against a
// stable target, or a POST whose spec declares it repeatable. Never for
// payments, order creation, or submissions.
//
// Note this also retries an error carrying no status (network error / timeout),
// where the write may have landed and only the response was lost. That is safe
// only because of the precondition above — which is why it is in the name.
//
// Same predicate as the query policy today; kept separate so the call site
// declares which obligation it is taking on, and so the two can diverge.
export const retryReplayableMutation = transientOnly

export const liveLessonRetryDelay = (failureCount: number): number =>
  Math.min(1000 * 2 ** failureCount, 4000)
